import {z} from "zod";

export const views = ["overview", "accounts", "syncs", "tasks", "history", "problems", "setup", "system"] as const;
export type View = (typeof views)[number];
export const viewNames: Record<View, string> = {
    overview: "Overview",
    accounts: "Connected accounts",
    syncs: "Data clusters",
    tasks: "Tasks",
    history: "Task history",
    problems: "Problems",
    setup: "Setup",
    system: "System status",
};
const optionalId = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
    .optional()
    .catch(undefined);
export const searchSchema = z.object({
    range: z.enum(["1h", "24h", "7d", "30d", "custom"]).catch("24h"),
    from: z.string().datetime({offset: true}).optional().catch(undefined),
    to: z.string().datetime({offset: true}).optional().catch(undefined),
    workspaceId: optionalId,
    connectorId: optionalId,
    connectionId: optionalId,
    syncId: optionalId,
    templateId: optionalId,
    pipelineId: optionalId,
    taskId: optionalId,
    search: z.string().max(200).optional().catch(undefined),
    sort: z.enum(["CREATED_DESC", "CREATED_ASC", "SCHEDULED_ASC", "STARTED_DESC", "FINISHED_DESC", "WAIT_DESC", "EXECUTION_DESC"]).catch("CREATED_DESC"),
    status: z.string().optional().catch(undefined),
    page: z.coerce.number().int().min(1).catch(1),
    includeDebug: z.boolean().catch(false),
    taskType: z.enum(["INITIALIZE", "REFRESH", "DELETE"]).optional().catch(undefined),
    includeOtherTaskTypes: z.boolean().catch(false),
    cursorType: z.enum(["INITIAL", "FUTURE", "HISTORICAL"]).optional().catch(undefined),
    mode: z.enum(["attempts", "tasks", "freshness", "connectors", "templates", "pipelines", "audit"]).optional().catch(undefined),
    detail: z.enum(["accounts", "syncs", "attempts", "tasks", "problems", "connectors", "templates", "pipelines"]).optional().catch(undefined),
    id: optionalId,
});
export type Filters = z.infer<typeof searchSchema>;
export type Row = Record<string, unknown>;
export const rowSchema = z.record(z.string(), z.unknown());
export const pageSchema = z.object({items: z.array(rowSchema), total: z.number()});
export type Page = z.infer<typeof pageSchema>;
export const record = (value: unknown): Row => (value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Row) : {});
export const text = (r: Row | undefined, key: string, fallback = "—"): string => (typeof r?.[key] === "string" ? r[key] : fallback);
export const number = (r: Row | undefined, key: string): number | null => (typeof r?.[key] === "number" && Number.isFinite(r[key]) ? r[key] : null);
export const rows = (r: Row | undefined, key = "items"): Row[] => (Array.isArray(r?.[key]) ? (r[key] as unknown[]).map(record) : []);
export const count = (n: number | null | undefined) =>
    n === null || n === undefined
        ? "—"
        : new Intl.NumberFormat(undefined, {maximumFractionDigits: 1, notation: Math.abs(n) >= 10000 ? "compact" : "standard"}).format(n);
export function duration(n: number | null | undefined): string {
    if (n === null || n === undefined) {
        return "Not recorded";
    }
    if (n < 1000) {
        return `${Math.round(n)} ms`;
    }
    if (n < 60000) {
        return `${(n / 1000).toFixed(1)} s`;
    }
    if (n < 3600000) {
        return `${(n / 60000).toFixed(1)} min`;
    }
    return `${(n / 3600000).toFixed(1)} h`;
}
// All dashboard timestamps use one explicit zone, with seconds and an offset label.
export const displayTimezone = "Asia/Kolkata";
export const date = (value: unknown) =>
    typeof value === "string" && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value))
        ? new Intl.DateTimeFormat("en-IN", {
              timeZone: displayTimezone,
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
          }).format(new Date(value)) + " IST"
        : "Not recorded";
export const chartTime = (value: number) =>
    new Intl.DateTimeFormat("en-IN", {
        timeZone: displayTimezone,
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(value);
export const human = (value: string) =>
    value
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/^./, (c) => c.toUpperCase());

export function timeWindow(f: Filters, now = Date.now()) {
    if (f.range === "custom") {
        if (f.from === undefined || f.to === undefined) {
            throw new Error("Choose both a start and end for the custom period.");
        }
        const span = Date.parse(f.to) - Date.parse(f.from);
        if (span < 60000 || span > 90 * 86400000 || Date.parse(f.to) > now) {
            throw new Error("Choose a past period between one minute and 90 days.");
        }
        return {from: f.from, to: f.to, bucketSeconds: span > 7 * 86400000 ? 86400 : span > 86400000 ? 3600 : span > 3600000 ? 900 : 60};
    }
    const end = Math.floor(now / 60000) * 60000;
    const span = {"1h": 3600000, "24h": 86400000, "7d": 7 * 86400000, "30d": 30 * 86400000}[f.range];
    return {
        from: new Date(end - span).toISOString(),
        to: new Date(end).toISOString(),
        bucketSeconds: f.range === "1h" ? 60 : f.range === "24h" ? 900 : f.range === "7d" ? 3600 : 86400,
    };
}
export function scope(f: Filters): Pick<Filters, "workspaceId" | "connectorId" | "connectionId" | "syncId" | "templateId" | "pipelineId" | "cursorType"> {
    return Object.fromEntries(
        Object.entries({
            workspaceId: f.workspaceId,
            connectorId: f.connectorId,
            connectionId: f.connectionId,
            syncId: f.syncId,
            templateId: f.templateId,
            pipelineId: f.pipelineId,
            cursorType: f.cursorType,
        }).filter(([, value]) => value !== undefined),
    );
}
export function catalogBody(f: Filters) {
    // Catalog/freshness APIs have no sync or pipeline filter; never silently claim those scopes.
    return {
        workspaceId: f.workspaceId,
        connectorId: f.connectorId,
        connectionId: f.connectionId,
        templateId: f.templateId,
        search: f.search,
        status: f.status,
        limit: 25,
        offset: (f.page - 1) * 25,
    };
}
export function analyticsBody(f: Filters) {
    return {...scope(f), ...timeWindow(f), includeDebug: f.includeDebug, limit: 25, offset: (f.page - 1) * 25};
}
export function timeBounds(f: Filters) {
    const {from, to} = timeWindow(f);
    return {from, to};
}
export const utcInput = (value: string | undefined) => (value === undefined ? "" : new Date(value).toISOString().slice(0, 16));
export function activityBody(f: Filters, active: boolean) {
    return {
        ...scope(f),
        ...(active ? {} : timeBounds(f)),
        includeDebug: f.includeDebug,
        search: f.search,
        sort: f.sort,
        state: f.status,
        taskId: f.taskId,
        taskType: f.taskType,
        includeOtherTaskTypes: f.includeOtherTaskTypes,
        limit: 25,
        offset: (f.page - 1) * 25,
    };
}
export function detailSearch(f: Filters, detail: NonNullable<Filters["detail"]>, id: string): Filters {
    return {...f, detail, id};
}
export function viewSearch(f: Filters, patch: Partial<Filters> = {}): Filters {
    return {
        ...f,
        page: 1,
        sort: "CREATED_DESC",
        search: undefined,
        status: undefined,
        taskId: undefined,
        taskType: undefined,
        includeOtherTaskTypes: false,
        mode: undefined,
        detail: undefined,
        id: undefined,
        ...patch,
    };
}

export function runTimes(value: string): string[] {
    const times = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    if (
        times.length === 0 ||
        times.some((v) => !/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(v)) ||
        new Set(times.map((v) => (v.length === 5 ? `${v}:00` : v))).size !== times.length
    ) {
        throw new Error("Enter distinct times as HH:mm or HH:mm:ss, separated by commas.");
    }
    return times;
}
